import React, { useCallback } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { DisplayData } from 'src/pages/Solutions/SolutionAssetHierarchy/SolutionAssetHierarchy';
import { Property } from './TempImagePropertyMapper';

interface PropertyItemProps {
  property: DisplayData;
  onVisibilityToggle: (propertyKey: string) => void;
}

const TempPropertyItem: React.FC<PropertyItemProps> = ({
  property,
  onVisibilityToggle,
}) => {
  const handleVisibilityToggle = useCallback(() => {
    onVisibilityToggle(property.key || '');
  }, [property.key, onVisibilityToggle]);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('propertyKey', property.key || '');
    e.dataTransfer.effectAllowed = 'move';
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px',
    backgroundColor: property.visible === false ? '#f5f5f5' : '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'move',
    transition: 'all 0.2s ease',
  };

  const leftSectionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  };

  const rightSectionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const nameStyle: React.CSSProperties = {
    fontWeight: 500,
    color: property.visible === false ? '#999' : '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const getSchemaColor = (schema: string) => {
    switch (schema) {
      case 'string':
        return { bg: '#f3e5f5', color: '#7b1fa2' };
      case 'integer':
        return { bg: '#e3f2fd', color: '#1976d2' };
      case 'double':
        return { bg: '#e8f5e8', color: '#2e7d32' };
      case 'boolean':
        return { bg: '#fff3e0', color: '#f57c00' };
      default:
        return { bg: '#f5f5f5', color: '#666' };
    }
  };

  const schemaColors = getSchemaColor(property.schema || '');

  const schemaChipStyle: React.CSSProperties = {
    height: '20px',
    fontSize: '10px',
    backgroundColor: schemaColors.bg,
    color: schemaColors.color,
  };

  return (
    <Box sx={containerStyle} draggable onDragStart={handleDragStart}>
      <Box sx={leftSectionStyle}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            flex: 1,
          }}
        >
          <Typography variant="body2" style={nameStyle}>
            {property.key}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: '#666', fontSize: '10px' }}
          >
            {property.name}
          </Typography>
        </Box>
        <Chip
          label={property.schema || 'empty'}
          size="small"
          sx={schemaChipStyle}
        />
      </Box>
      <Box sx={rightSectionStyle}>
        <IconButton
          size="small"
          onClick={handleVisibilityToggle}
          style={{
            color: property.visible === false ? '#999' : '#1976d2',
            padding: '4px',
          }}
        >
          {property.visible === false ? (
            <VisibilityOff fontSize="small" />
          ) : (
            <Visibility fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default TempPropertyItem;
